import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { adminPatientDetailApi } from "apis";
import { useToast } from "contexts/ToastContext";

export const ADMIN_PROCEDURES_BY_PATIENT_QUERY_KEY = "adminProceduresByPatient";

export interface UseAdminProceduresByPatientParams {
  patientId: string;
  page?: number;
  pageSize: number;
  enabled?: boolean;
}

export const useAdminProceduresByPatient = ({
  patientId,
  page = 1,
  pageSize,
  enabled = true,
}: UseAdminProceduresByPatientParams) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      ADMIN_PROCEDURES_BY_PATIENT_QUERY_KEY,
      patientId,
      page,
      pageSize,
    ],
    queryFn: () =>
      adminPatientDetailApi.getProcedureByPatientIdForAdmin({
        patientId,
        page,
        pageSize,
      }),
    placeholderData: keepPreviousData,
    enabled: enabled && !!patientId,
  });

  return {
    procedures: data?.procedures ?? [],
    pagination: data?.pagination,
    isLoading,
  };
};

export const useAdminDeleteProcedure = (patientId: string) => {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (procedureId: string) =>
      adminPatientDetailApi.deleteProcedureForAdmin({
        patientId,
        procedureId,
      }),
    onSuccess: () => {
      showSuccess("Success", "Procedure deleted successfully.");
      queryClient.invalidateQueries({
        queryKey: [ADMIN_PROCEDURES_BY_PATIENT_QUERY_KEY, patientId],
      });
    },
    onError: () => {
      showError("Error", "Failed to delete procedure. Please try again.");
    },
  });
};

export const useAdminToggleProcedureVisibility = (patientId: string) => {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      procedureId,
      isHiddenFromPatient,
    }: {
      procedureId: string;
      isHiddenFromPatient: boolean;
    }) =>
      adminPatientDetailApi.toggleProcedureVisibilityForAdmin({
        patientId,
        procedureId,
        toggleProcedureVisibilityDto: { isHiddenFromPatient },
      }),
    onSuccess: (_, variables) => {
      const message = variables.isHiddenFromPatient
        ? "Procedure hidden from patient."
        : "Procedure is now visible to the patient.";
      showSuccess("Success", message);
      queryClient.invalidateQueries({
        queryKey: [ADMIN_PROCEDURES_BY_PATIENT_QUERY_KEY, patientId],
      });
    },
    onError: () => {
      showError(
        "Error",
        "Failed to update procedure visibility. Please try again.",
      );
    },
  });
};
